import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { Newspaper } from "lucide-react"; // Importujemy ikonę z lucide-react
import keyframes from "styled-components";
// --- STYLED COMPONENTS ---
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const PageHeader = styled.header`
  padding: 50px 0 60px 0;
  border-bottom: 2px solid #111;
  margin-bottom: 60px;
  animation: ${slideUp} 0.6s ease-out forwards;

  @media (max-width: 768px) {
    padding: 120px 0 40px 0;
    margin-bottom: 40px;
  }

  h1 {
    font-size: clamp(3rem, 8vw, 6rem);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: -3px;
    margin: 0;
    line-height: 0.9;
    span {
       color: #888;
       display: block;
    }
  }
`;
const NewsListWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 100px 5%;
  background-color: #ffffff;
box-sizing: border-box;
  @media (max-width: 768px) {
    padding: 60px 5%;
  }
`;

const NewsSectionHeader = styled.h1`
  font-size: clamp(2.5rem, 6vw, 5rem);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -2px;
  color: #111;
  margin-bottom: 80px;
  border-bottom: 2px solid #111;
  padding-bottom: 20px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
    padding-bottom: 15px;
  }
`;

const NewsArticle = styled.section`
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 50px;
  padding: 60px 0;
  border-bottom: 1px solid #eee;
  transition: all 0.3s ease;
  text-align: left;

  /* Przejście na układ pionowy na tabletach i smartfonach */
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 40px 0;
  }

  @media (max-width: 480px) {
    gap: 20px;
    padding: 30px 0;
  }
`;

const NewsVisual = styled.div`
  width: 100%;
  height: 250px;
  background: #f9f9f9;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 1px solid #eee;

  @media (max-width: 768px) {
    height: 200px; /* Zmniejszenie wysokości obrazka na telefonach */
  }
`;

const NewsImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.25, 1, 0.5, 1), filter 0.6s ease;
`;

const NewsFallbackIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #f9f9f9;
  
  svg {
    width: 60px;
    height: 60px;
    color: #ccc;
    transition: all 0.4s ease;
  }
`;

const NewsContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const NewsMeta = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;

  @media (max-width: 480px) {
    margin-bottom: 10px;
    font-size: 0.8rem;
  }
`;

const NewsTitle = styled.h2`
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  font-weight: 800;
  color: #000;
  margin: 0 0 20px 0;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: -1px;

  @media (max-width: 480px) {
    margin: 0 0 15px 0;
  }
`;

const NewsText = styled.div`
  font-size: 1.1rem;
  color: #555;
  line-height: 1.7;
  margin-bottom: 25px;
  
  /* Logika zwijania tekstu */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  
  ${(props) =>
    props.isExpanded
      ? `
      -webkit-line-clamp: unset;
      display: block;
    `
      : `
      -webkit-line-clamp: 4; /* Pokaże tylko 4 linijki, potem doda ... */
    `}

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 20px;
  }
`;

const NewsToggleBtn = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  font-size: 0.9rem;
  font-weight: 800;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: #000;
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

// --- KOMPONENT ---

export const AktualnoscFullBox = () => {
  const [aktualnosci, setAktualnosci] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const textRefs = useRef({});
  const [overflowing, setOverflowing] = useState({});
  const location = useLocation();

  // Zmień na swój właściwy adres API
  const adres = "https://api.pth-hokej.pl";

  /* ---------------- fetch aktualności ---------------- */
  const fetchAktualnosci = async () => {
    try {
      const res = await fetch(`${adres}/api/aktualnosci`);
      if (!res.ok) throw new Error("Błąd sieci");
      const data = await res.json();
      setAktualnosci(data);
      localStorage.setItem("aktualnosci", JSON.stringify(data));
    } catch (err) {
      console.error("❌ Błąd przy pobieraniu aktualności:", err);
      const cached = localStorage.getItem("aktualnosci");
      if (cached) {
        setAktualnosci(JSON.parse(cached));
      }
    }
  };

  /* ---------------- inicjalizacja ---------------- */
  useEffect(() => {
    const cached = localStorage.getItem("aktualnosci");
    if (cached) {
      setAktualnosci(JSON.parse(cached));
    }
    fetchAktualnosci();
  }, []);

  /* ---------------- sprawdzanie overflow ---------------- */
  useEffect(() => {
    const checkOverflow = () => {
      const result = {};
      aktualnosci.forEach((akt) => {
        const el = textRefs.current[akt._id];
        if (el) {
          result[akt._id] = el.scrollHeight > el.clientHeight;
        }
      });
      setOverflowing(result);
    };

    setTimeout(checkOverflow, 100);
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [aktualnosci, expandedId]);

  /* ---------------- przewijanie do hash-a ---------------- */
  useEffect(() => {
    if (location.hash && aktualnosci.length > 0) {
      const id = location.hash.replace("#", "");

      // Dajemy Reactowi 150ms na ułożenie elementów na ekranie
      setTimeout(() => {
        const articleElement = document.getElementById(id);
        const textElement = textRefs.current[id]; // Pobieramy bezpośrednio element tekstu z DOM

        if (articleElement) {
          // 1. Płynne przewijanie do kafelka
          articleElement.scrollIntoView({ behavior: "smooth", block: "start" });

          // 2. Sprawdzamy na żywo, czy tekst wychodzi poza dozwolone 4 linijki (overflow)
          if (textElement && textElement.scrollHeight > textElement.clientHeight) {
            // Tylko wtedy rozwijamy artykuł!
            setExpandedId(id);
          }
        }
      }, 150);
    }
  }, [location, aktualnosci]);

  /* ---------------- render ---------------- */
  return (
    <NewsListWrapper>
      <PageHeader>
        <h1>
          Aktualności
        </h1>
      </PageHeader>
      {Array.isArray(aktualnosci) && aktualnosci.length > 0 ? (
        aktualnosci.map((aktualnosc) => {
          const isExpanded = expandedId === aktualnosc._id;
          const hasOverflow = overflowing[aktualnosc._id];

          return (
            <NewsArticle id={aktualnosc._id} key={aktualnosc._id}>
              {/* Sekcja Wizualna */}
              <NewsVisual>
                {aktualnosc.zdjecie ? (
                  <NewsImage
                    src={aktualnosc.zdjecie}
                    alt={aktualnosc.tytul}
                    className="news-image"
                  />
                ) : (
                  // Wyświetlanie ikony w przypadku braku zdjęcia
                  <NewsFallbackIconWrapper>
                    <Newspaper className="fallback-icon" />
                  </NewsFallbackIconWrapper>
                )}
              </NewsVisual>

              {/* Sekcja Treści */}
              <NewsContent>
                <NewsMeta>{aktualnosc.data}</NewsMeta>
                <NewsTitle>{aktualnosc.tytul}</NewsTitle>

                <NewsText
                  ref={(el) => (textRefs.current[aktualnosc._id] = el)}
                  isExpanded={isExpanded}
                >
                  {aktualnosc.tresc}
                </NewsText>

                {/* Przyciski Zwiń / Rozwiń */}
                {hasOverflow && !isExpanded && (
                  <NewsToggleBtn onClick={() => setExpandedId(aktualnosc._id)}>
                    Czytaj dalej &darr;
                  </NewsToggleBtn>
                )}
                {isExpanded && (
                  <NewsToggleBtn onClick={() => setExpandedId(null)}>
                    Zwiń &uarr;
                  </NewsToggleBtn>
                )}
              </NewsContent>
            </NewsArticle>
          );
        })
      ) : (
        <p style={{ color: "#999", fontSize: "1.2rem" }}>Brak aktualności do wyświetlenia.</p>
      )}
    </NewsListWrapper>
  );
};